import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select, Checkbox } from '../ui';
import { DynamicTable, Column } from './DynamicTable';
import { CROP_OPTIONS } from '../../utils/constants';
import type { ProductionData, Stock, CropType } from '../../types';

// Cria um novo item de estoque com valores padrão
const createNewStock = (): Stock => ({
  id: `stock-${Date.now()}`,
  crop: 'soja',
  volume: 0,
  warehouse: '',
});

export interface ProductionSectionProps {
  data: Partial<ProductionData>;
  onChange: (data: Partial<ProductionData>) => void;
  onStockChange: (stock: Stock[]) => void;
  naFields: Record<string, boolean>;
  onNaChange: (key: string, value: boolean) => void;
  disabled?: boolean;
}

export const ProductionSection: React.FC<ProductionSectionProps> = ({
  data,
  onChange,
  onStockChange,
  naFields,
  onNaChange,
  disabled = false,
}) => {
  const { t } = useTranslation();

  // Colunas da tabela de estoque — recalcular apenas quando disabled muda
  const stockColumns: Column<Stock>[] = useMemo(
    () => [
      {
        key: 'crop',
        header: t('production.crop'),
        width: '35%',
        render: (item, _index, onItemChange) => (
          <Select
            value={item.crop}
            onChange={(value) => onItemChange({ ...item, crop: value as CropType })}
            options={CROP_OPTIONS}
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'volume',
        header: t('production.volumeTons'),
        width: '30%',
        render: (item, _index, onItemChange) => (
          <Input
            value={item.volume?.toString() || ''}
            onChange={(value) => onItemChange({ ...item, volume: parseFloat(value) || 0 })}
            type="number"
            placeholder="0"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'warehouse',
        header: t('production.warehouse'),
        width: '35%',
        render: (item, _index, onItemChange) => (
          <Input
            value={item.warehouse}
            onChange={(value) => onItemChange({ ...item, warehouse: value })}
            placeholder="Nome do armazém"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
    ],
    [disabled, t]
  );

  // Handler para seleção/desmarcação de culturas
  const handleCropToggle = (crop: CropType, checked: boolean) => {
    const currentCrops = data.selectedCrops || [];
    const newCrops = checked
      ? [...currentCrops, crop]
      : currentCrops.filter((c) => c !== crop);

    onChange({ ...data, selectedCrops: newCrops });
  };

  // Handler para checkbox de pecuária
  const handleCattleChange = (checked: boolean) => {
    onChange({ ...data, hasCattle: checked });
  };

  const isStockNa = naFields['production.stock'] ?? false;

  return (
    <div className="space-y-6">
      {/* Seleção de culturas */}
      <div>
        <p className="text-sm text-gray-600 mb-4">
          {t('production.selectCrops')}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {CROP_OPTIONS.map((crop) => (
            <Checkbox
              key={crop.value}
              label={crop.label}
              checked={data.selectedCrops?.includes(crop.value) || false}
              onChange={(checked) => handleCropToggle(crop.value, checked)}
              disabled={disabled}
            />
          ))}
        </div>

        <div className="mt-3">
          <Checkbox
            label={t('production.cattle')}
            checked={data.hasCattle || false}
            onChange={handleCattleChange}
            disabled={disabled}
          />
        </div>
      </div>

      {/* Estoque armazenado */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-700">{t('production.stock')}</h4>
          <Checkbox
            label={t('common.na')}
            checked={isStockNa}
            onChange={(checked) => onNaChange('production.stock', checked)}
            disabled={disabled}
          />
        </div>

        {!isStockNa && (
          <DynamicTable
            columns={stockColumns}
            data={data.stock || []}
            onChange={onStockChange}
            createNewItem={createNewStock}
            addButtonText={t('production.addStock')}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
};

export default ProductionSection;
